/**
 * Madesa Site Map Component
 * 
 * A custom element that enhances the site map visualization by adding
 * structural classes to the last leaf nodes of the menu tree.
 * This enables correct CSS styling for the tree connector lines.
 *
 * @example
 * <madesa-site-map level-selector=".level-1"></madesa-site-map>
 */
class MadesaSiteMap extends HTMLElement {
  constructor() {
    super();
    this._connected = false;
  }

  /**
   * Lifecycle callback invoked when the element is added to the DOM.
   * Includes a check to prevent unnecessary re-initialization.
   */
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this.init();
  }

  /**
   * Lifecycle callback invoked when the element is removed from the DOM.
   * Resets the connected state.
   */
  disconnectedCallback() {
    this._connected = false;
  }

  /**
   * Returns a list of attributes to observe.
   * @returns {string[]}
   */
  static get observedAttributes() {
    return ['level-selector'];
  }

  /**
   * Lifecycle callback invoked when an observed attribute is changed.
   * @param {string} name - The name of the attribute.
   * @param {string} oldValue - The old value of the attribute.
   * @param {string} newValue - The new value of the attribute.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'level-selector' && oldValue !== newValue) {
      this.init();
    }
  }

  /**
   * Initializes the component logic.
   * Finds all top-level menu items and marks their last leaf nodes.
   * Idempotent: cleans up previous state before applying new one.
   */
  init() {
    try {
      const root = this;
      const selector = root.getAttribute('level-selector') || '.level-1';
      const rootLevels = root.querySelectorAll(selector);

      if (!rootLevels.length) {
        return;
      }

      // Cleanup: Remove existing classes only within the relevant levels.
      // This is more performant than a global querySelectorAll on the root.
      rootLevels.forEach((level1) => {
        if (!(level1 instanceof HTMLElement)) return;
        
        const marked = level1.querySelectorAll('.level-last-link');
        marked.forEach(el => el.classList.remove('level-last-link'));
      });

      // Apply logic to find and mark the last leaf
      rootLevels.forEach((level1) => {
        if (!(level1 instanceof HTMLElement)) return;
        
        const lastLeaf = this.getLastLeaf(level1);
        if (lastLeaf) {
          lastLeaf.classList.add('level-last-link');
        }
      });

    } catch (error) {
      console.error('MadesaSiteMap: Error initializing component', error);
    }
  }

  /**
   * Recursively finds the last leaf node in a nested list structure.
   * Optimized for performance using direct DOM navigation.
   * @param {HTMLElement} li - The current list item to search.
   * @returns {Element | null} The last leaf element (usually an anchor or span).
   * @private
   */
  getLastLeaf(li) {
    if (!li) return null;

    // Performance optimization: Iterate over children instead of querySelector
    // to find the sublist. This avoids expensive CSS selector parsing.
    let sublist = null;
    for (const child of li.children) {
      // Semantic check: ensure it is a UL with the correct class
      if (child.tagName === 'UL' && child.classList.contains('site-map-sublist')) {
        sublist = child;
        break;
      }
    }

    // If no sublist, this item is a leaf
    if (!sublist) {
      // Robust fallback: look for standard link class, or generic a/span tags
      return li.querySelector(':scope > .site-map-link, :scope > a, :scope > span');
    }

    // Get the last child li directly from the sublist
    const lastLi = sublist.lastElementChild;
    
    // Recurse into the last child
    if (lastLi instanceof HTMLElement) {
      return this.getLastLeaf(lastLi);
    }
    
    return null;
  }
}

// Register the custom element if it hasn't been registered yet
if (!customElements.get('madesa-site-map')) {
  customElements.define('madesa-site-map', MadesaSiteMap);
}
